import { memo, useMemo, useState } from 'react';
import { ContentItemBoxListContainer } from 'ContentItemBox';
import { type StreamForDashboard } from 'Lessons';
import { regionAwareImageUrlForFormat } from 'regionAwareImage';
import { BlockLink } from 'BlockLink';
import { useTranslation } from 'react-i18next';
import './locales/courseItemBox/courseItemBox-en.json';
import { type LearnerProject } from 'LearnerProjects';
import { ProjectContentBox } from 'ProjectContentBox';
import { frontRoyalStoreApi } from 'FrontRoyalStore';
import { CourseItemBox } from './CourseItemBox';
import { type GetProgressInfo } from './CourseItemBox.types';

const initialCourseLimit = 4;

export const CourseList = memo(
    ({
        courses,
        projects,
        header,
        onPress,
        getProgressInfo,
        initiallyLimitShownCourses = false,
        fullWidth = false,
        showSubtitle = false,
        availableOffline = false,
    }: {
        courses: StreamForDashboard[];
        projects?: LearnerProject[];
        header?: string | null;
        onPress: (course: StreamForDashboard) => void;
        getProgressInfo: GetProgressInfo;
        initiallyLimitShownCourses?: boolean;
        fullWidth?: boolean;
        showSubtitle?: boolean;
        availableOffline?: boolean;
    }) => {
        const { t } = useTranslation('back_royal', { keyPrefix: 'courseItemBox.courseItemBox' });
        const [limitShownCourses, setLimitShownCourses] = useState(
            initiallyLimitShownCourses && courses.length > initialCourseLimit,
        );
        const { data: publishedStreamIdsWithAllContentStored = {} } =
            frontRoyalStoreApi.usePublishedStreamIdsWithAllContentStoredQuery();

        const shownCourses = useMemo(
            () => (limitShownCourses ? courses.slice(0, initialCourseLimit) : courses),
            [limitShownCourses, courses],
        );

        if (shownCourses.length === 0 && !projects?.length) return null;

        return (
            <div>
                <ContentItemBoxListContainer header={header} fullWidth={fullWidth} tabletGapY="2.5">
                    {shownCourses.map(course => (
                        <CourseItemBox
                            key={course.id}
                            courseId={course.id}
                            showSubtitle={showSubtitle}
                            title={course.title}
                            src={regionAwareImageUrlForFormat(course.image, 'original')!}
                            progressInfo={getProgressInfo({ type: 'stream', content: course })}
                            onPress={() => onPress(course)}
                            availableOffline={availableOffline && !!publishedStreamIdsWithAllContentStored[course.id]}
                        />
                    ))}
                    {projects?.map(project => (
                        <ProjectContentBox project={project} fullWidth={fullWidth} key={project.id} />
                    ))}
                </ContentItemBoxListContainer>

                {limitShownCourses && (
                    <div className="mt-3.5 flex justify-center">
                        <BlockLink
                            // FIXME: This is a quick hack to get around the problem discussed at https://github.com/heroui-inc/heroui/issues/1934
                            onPress={() => setTimeout(() => setLimitShownCourses(false))}
                            anchorIcon="chevron-down"
                            color="slate-grey"
                        >
                            {t('showMore')}
                        </BlockLink>
                    </div>
                )}
            </div>
        );
    },
);
